import { Star } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useTranslation } from "@/lib/i18n";

const testimonialAuthors = [
  {
    author: "Sarah M.",
    location: "Toronto, ON",
    rating: 5,
    photo:
      "https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?w=112&h=112&fit=crop&crop=faces&auto=format",
  },
  {
    author: "Mike P.",
    location: "Vancouver, BC",
    rating: 5,
    photo:
      "https://images.unsplash.com/photo-1566492031773-4f4e44671857?w=112&h=112&fit=crop&crop=faces&auto=format",
  },
  {
    author: "Jennifer L.",
    location: "Calgary, AB",
    rating: 5,
    photo:
      "https://images.unsplash.com/photo-1567532939604-b6b5b0db2604?w=112&h=112&fit=crop&crop=faces&auto=format",
  },
];

export function Testimonials() {
  const { t } = useTranslation();

  const benefits = [
    t("testimonials.smarterClearerReceipts"),
    t("testimonials.easierReturns"),
    t("testimonials.warrantyReminders"),
    t("testimonials.lessStressMoreConfidence"),
  ];

  const testimonials = testimonialAuthors.map((item, index) => ({
    ...item,
    quote: t(`testimonials.testimonial${index + 1}`),
  }));

  return (
    <section className="py-20 sm:py-28 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section heading */}
        <div className="max-w-3xl mx-auto text-center mb-14 animate-fade-up">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t("testimonials.whyUsersLove").split("{love}")[0]}
            <span className="text-primary">{t("testimonials.love")}</span>
            {t("testimonials.whyUsersLove").split("{love}")[1] || ""}
          </h2>
        </div>

        {/* Benefits pills */}
        <div className="flex flex-wrap justify-center gap-3 mb-14 max-w-3xl mx-auto animate-fade-up stagger-1">
          {benefits.map((benefit) => (
            <span
              key={benefit}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium"
            >
              {benefit}
            </span>
          ))}
        </div>

        {/* Testimonials grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <Card
              key={testimonial.author}
              className={`glass-card hover-lift animate-fade-up stagger-${index + 2} relative overflow-hidden`}
            >
              <CardContent className="p-6 pt-8 relative">
                {/* Decorative quotation mark */}
                <span
                  className="absolute top-2 left-4 font-heading text-6xl text-primary/10 leading-none select-none pointer-events-none"
                  aria-hidden="true"
                >
                  &ldquo;
                </span>

                {/* Star rating */}
                <div className="flex gap-1 mb-5 relative z-10">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 text-accent fill-accent"
                    />
                  ))}
                </div>

                {/* Quote */}
                <blockquote className="font-heading text-foreground mb-6 leading-relaxed relative z-10">
                  {testimonial.quote}
                </blockquote>

                {/* Author */}
                <div className="flex items-center gap-3 relative z-10">
                  <img
                    src={testimonial.photo}
                    alt={testimonial.author}
                    loading="lazy"
                    className="w-10 h-10 rounded-full object-cover ring-2 ring-primary/30 shrink-0"
                  />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {testimonial.author}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {testimonial.location}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}
